import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { Badge, Button, Select, Tag } from "antd";
import {
  ExportOutlined,
  HomeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router";
import Table, { TableProps } from "../../Common/Table";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import { RequestData, ParamsType } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { useUsersTableGet, UserTableRow } from "../../../hooks/api/users";
import {
  queryFilterParams,
  getSorterParams,
  getUserRequestPath,
} from "../../../utils";
import {
  UserPlanDisplay,
  UserRoleDisplay,
  UserStatusDisplay,
} from "../../../enums/user";
import { useAuth, UserRoleType, UserStatusType } from "../../../store/auth";
import { dateTimeFormat } from "../../../contstant";
import { IconExport } from "../../Common/Icon";
import CreateModal from "./Create";
import EditModal from "./Edit";
import { useStaffTableGet } from "../../../hooks/api/staff";

export enum UserStatusColor {
  "active" = "green",
  "pending" = "blue",
  "deactivated" = "orange",
  "banned" = "red",
  "Deleted" = "gray",
}

export function getUserStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  return UserStatusColor[status?.toLowerCase()] || "rgba(0,0,0,.15)";
}
const columns = (t: TFunction): ProColumns<UserTableRow>[] => [
  {
    title: "Full Name",
    dataIndex: "fullName",
    key: "fullName",
    sorter: true,
    hideInSearch: true,
  },
  {
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    sorter: false,
    hideInSearch: true,
  },
  {
    title: t("email"),
    dataIndex: "email",
    key: "email",
    hideInSearch: true,
    sorter: false,
  },
  {
    title: "Active Vaads",
    dataIndex: "activeVaadsCount",
    key: "activeVaadsCount",
    sorter: true,
    hideInSearch: true,
    render: (_, record) => <div>{record.memberships?.length}</div>,
  },
  {
    title: t("pages.users.role"),
    dataIndex: "role",
    key: "role",
    sorter: true,
    valueEnum: UserRoleDisplay,
    render: (_, record) => {
      const role = record.role;
      const roleConfig: Record<string, { color: string; text: string }> = {
        admin: { color: "#722ED1", text: "Admin" },
        leader: { color: "#EB2F96", text: "Leader" },
        super_admin: { color: "orange", text: "Super Admin" },
        student: { color: "green", text: "Student" },
      };
      const config = roleConfig[role?.toLowerCase()] || {
        color: "default",
        text: role,
      };

      return (
        <Tag color={config.color} variant="outlined">
          {config.text}
        </Tag>
      );
    },
    renderFormItem: (_, { defaultRender, ...config }) => (
      <Select
        {...config}
        placeholder={t("placeholder.select")}
        showSearch={{ optionFilterProp: "label" }}
        value={
          Array.isArray(config.value) ? config.value : config.value?.split(",")
        }
      />
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: true,
    valueEnum: UserStatusDisplay,
    render: (_, record) => (
      <Badge
        color={getUserStatusColor(record.status)}
        text={<span className="capitalize-first-letter">{record.status}</span>}
      />
    ),
  },
];

function ToolBar({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuth();

  if (
    !user?.role ||
    getUserRequestPath(user.role as UserRoleType) !== "admin"
  ) {
    return null;
  }
  return [
    <Button
      key="create"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        onCreate();
      }}
    >
      Add User
    </Button>,
  ];
}

export default function StaffManagementContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const usersGet = useStaffTableGet();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<string | undefined>();
  const { user } = useAuth();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  const actions: TableProps<UserTableRow, ParamsType, "text">["actions"] = [
    [
      "edit",
      ({ record }: { record: UserTableRow }) => ({
        title: "Edit user",
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          setEditUserId(record.id);
          setEditOpen(true);
        },
      }),
    ],
  ];

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: t("routes.users") },
    ]);
  }, [i18n.language, t]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserTableRow>>> => {
    const newParams = queryFilterParams({
      orderBy: "DESC",
      // orderByColumn: "createdAt",
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "20",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });
    return usersGet
      .fetch({
        ...newParams,
        // role: undefined,
        // roles: newParams.role ? newParams?.role?.split(",") : undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <>
      <Table<UserTableRow>
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle={t("pages.users.tableName")}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={() => setCreateOpen(true)} />,
        ]}
        request={tableRequest}
        columns={columns(t)}
        onRow={(record) => ({
          onClick: () => navigate(`${record.id}/view`), // или `${record.id}/view`
          style: { cursor: "pointer" },
        })}
        {...(isAdmin ? { actions } : {})}
        search={{
          style: {
            // display: 'none',
          },
        }}
        options={{
          search: {
            name: "search",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-users",
          persistenceType: "localStorage",
        }}
      />
      <CreateModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        setTableKey={() => setTableKey(Date.now())}
      />
      <EditModal
        key={editUserId}
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditUserId(undefined);
        }}
        setTableKey={() => setTableKey(Date.now())}
        userId={editUserId}
      />
    </>
  );
}
